import { useState, useEffect } from "react";
import { Box } from "@mui/system";
import { useMediaQuery, useTheme, Grid } from "@mui/material";
import { useForm } from "react-hook-form";

import SimpleTable from "../../components/tables/simpleTable";
import SimpleSelect from "../../components/selects/simpleSelect";
import FormCalendar from "../../components/calendar/formCalendar";
import LaddaButton from "../../components/buttons/laddaButton";

import {
  callApiAsync,
  getAllTaskManagerUsers,
  getDailyTasksByUser,
  getNonBusinessDay,
  postJustifyTaskDay
} from "../../services/apiService";

import { getCookie } from "../../utils/getCookie";
import { icons } from "../../constants";
import JustifyTaskModal from "./justifyTaskModal";
import BreadCrumbComp from "../../components/breadCrumb";

const JustifiedActivities = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const today = new Date();

  const userSesionData = getCookie("sesion");
  const user_id = userSesionData?.id_usuario ?? null;

  const [disabledDates, setDisabledDates] = useState([]);
  const [userList, setUserList] = useState([]);
  const [rows, setRows] = useState([]);
  const [openJustify, setOpenJustify] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const getNonBusinessDaysData = async () => {
    await callApiAsync(
      () => getNonBusinessDay(),
      (data) => {
        setDisabledDates(data);
      }
    );
  };

  const getAssignableUsersData = async () => {
    await callApiAsync(
      () => getAllTaskManagerUsers(),
      (data) => {
        setUserList(data);
      }
    );
  };

  const openJustifyModal = (row) => {
    setSelectedTask(row);
    setOpenJustify(true);
  };

  // ==============================
  //  Form
  // ==============================
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      employee_id: "",
      scope: "day",
      ref_date: today
    }
  });

  const watchedEmployeeId = watch("employee_id");

  const columns = [
    { field: "date", description: "date", header: "Fecha" },
    { field: "description", header: "Descripción" },
    { field: "process", header: "Proceso" },
    { field: "start", header: "Hora inicio" },
    { field: "end", header: "Hora fin" },
    { field: "actionButtons", columnType: "actionButtons" }
  ];

  const onSubmit = async (data) => {
    const employeeId = data.employee_id || String(user_id);
    const ref = data.ref_date || today;
    const dateStr = ref.toISOString().slice(0, 10); // YYYY-MM-DD

    await callApiAsync(
      () => getDailyTasksByUser({ user_id: employeeId, date: dateStr }),
      (resp) => {
        const mapped =
          (resp.items || []).map((t) => ({
            // info visible en la tabla
            date: (t.task_day?.date || dateStr)?.slice(0, 10),
            description: t.description,
            process: t.process_name,
            start: t.task_day?.start_hour,
            end: t.task_day?.end_hour,
            duration: t.task_day?.completed_time,
            // info interna útil para el back
            task_manager_id: t.id,
            task_day_id: t.task_day?.id,
            employee_id: t.employer_in_charge
          })) ?? [];

        setRows(mapped);
      }
    );
  };

  const handleClear = () => {
    reset({
      employee_id: "",
      scope: "day",
      ref_date: today
    });
    setRows([]);
  };

  useEffect(() => {
    getNonBusinessDaysData();
    getAssignableUsersData();
  }, []);

  const defaultAssigneeId =
    watchedEmployeeId || (user_id ? String(user_id) : "");

  // ==============================
  //  Enviar justificación al back
  // ==============================
  const handleConfirmJustify = async (formPayload) => {
    if (!selectedTask) return;

    const dataToSend = {
      task_day_id: selectedTask.task_day_id,
      reason_id: formPayload.reason_id,
      comment: formPayload.comment,
      assignee_id: formPayload.assignee_id,
      user_id
    };
    await callApiAsync(
      () => postJustifyTaskDay(dataToSend),
      (resp) => {
        setOpenJustify(false);
        handleSubmit(onSubmit)();
      }
    );
  };

  return (
    <Box
      display="flex"
      minHeight="100vh"
      bgcolor="#f9f9ff"
      padding={"3vh"}
      flexDirection={"column"}
      sx={{ flexWrap: isMobile ? "wrap" : "nowrap" }}
    >
      {/* Título */}
              <BreadCrumbComp />
      <div className="j-space-between w-100p">
        <div className="searchTitle align-center w-100p">
          <div className="j-space-between w-100p">
            <h1>ACTIVIDADES JUSTIFICADAS</h1>
            <div className="HContainer">
            </div>
          </div>
        </div>
      </div>

      {/* Filtros + Tabla */}
      <Box
        sx={{
          width: "100%",
          bgcolor: "#ffffff",
          borderRadius: 2,
          padding: 2,
          marginTop: 2,
          marginBottom: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)"
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <p className="blueLabelForm">Persona / Empleado</p>
              <SimpleSelect
                name="employee_id"
                control={control}
                options={userList}
                margin={"0"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <p className="blueLabelForm">Fecha de referencia</p>
              <FormCalendar
                name="ref_date"
                control={control}
                width={"100%"}
                defaultValue={today}
                margin={"0"}
                disableWeekends={true}
                disabledDates={disabledDates}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                  mt: { xs: 2, md: 4 }
                }}
              >
                <LaddaButton
                  type="submit"
                  variant="contained"
                  width="auto"
                  height="40px"
                >
                  <img
                    className="btn-icon"
                    src={icons.filterWhite}
                    alt="icon"
                  />
                </LaddaButton>

                <LaddaButton
                  type="button"
                  variant="outlined"
                  width="auto"
                  height="40px"
                  onClick={handleClear}
                >
                  <img
                    className="btn-icon"
                    src={icons.clearFilterWhite}
                    alt="icon"
                  />
                </LaddaButton>
              </Box>
            </Grid>
          </Grid>
        </form>

        <SimpleTable
          columns={columns}
          data={rows}
          isPaginated={false}
          scrollHeight="60vh"
          globalFilterFields={["description", "process", "date"]}
          emptyMessage={
            "No hay actividades justificadas para los filtros seleccionados"
          }
          buttons={[
            {
              type: "icon",
              icon: icons.cambiarUsuario,
              onClick: (data) => {
                openJustifyModal(data);
              }
            }
          ]}
        />
      </Box>

      <JustifyTaskModal
        open={openJustify}
        handleClose={() => setOpenJustify(false)}
        task={selectedTask}
        reasonsOptions={[
          { name: "Capacitación", value: "training" },
          { name: "Reunión", value: "meeting" }
        ]}
        assignableUsers={userList}
        defaultAssigneeId={defaultAssigneeId}
        onConfirm={handleConfirmJustify}
      />
    </Box>
  );
};

export default JustifiedActivities;
